import {useState} from 'react';
import isEqual from 'react-fast-compare';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible as isInaccessibleGQL} from 'graphql-client/InaccessibleInfo';
import APIUtil from 'util/APIUtil';

import {SourceSampleSelectionTableQuery} from './sample-selection/__generated/SourceSampleSelectionTableQuery';
import {SOURCE_SAMPLE_SELECTION_TABLE_QUERY} from './sample-selection/useSampleQueryData';

/**
 * This hook is used by the AddSamplesFromWorklistModal & AddSamplesFromBarcodeScannerModal components.
 * It hydrates the sample data, but does not return them. (They simply need to be available in the store.)
 *
 * If the query has been successfully completed, we also return a mapping of {sampleId: schemaId}
 */

export default function useHydrateWorklistSamples(sampleIds: Array<string>): {
  status: QueryResultType;
  schemaIdBySampleId: {[sampleId: string]: string};
} {
  const queryResult = useBenchlingQuery<SourceSampleSelectionTableQuery>(
    SOURCE_SAMPLE_SELECTION_TABLE_QUERY,
    {
      variables: {
        bioentityIds: sampleIds.filter((id) => APIUtil.isBiologicalEntity(id)),
        boxIds: [],
        containerIds: sampleIds.filter((id) => APIUtil.isContainer(id)),
        plateIds: sampleIds.filter((id) => APIUtil.isPlate(id)),
      },
    }
  );
  const [schemaIdBySampleId, setSchemaIdsBySampleId] = useState<{[sampleId: string]: string}>({});

  // We prepare the map of {sampleId: schemaId} for all completed queries
  if (queryResult.type === QueryResultType.SUCCESS) {
    const newSchemaIdBySampleId: {[sampleId: string]: string} = {};
    for (const bioentity of queryResult.data.bioentities) {
      if (!isInaccessibleGQL(bioentity)) {
        const sample = graphQLToREST('BioentitySummary', bioentity);
        const schemaId = sample.tagSchema?.id;
        if (schemaId != null) {
          newSchemaIdBySampleId[sample.id] = schemaId;
        }
      }
    }

    for (const container of queryResult.data.containers) {
      if (!isInaccessibleGQL(container)) {
        const sample = graphQLToREST('Container', container);
        newSchemaIdBySampleId[sample.id] = sample.storableSchema.id;
      }
    }

    for (const plate of queryResult.data.plates) {
      if (!isInaccessibleGQL(plate)) {
        const sample = graphQLToREST('PlateSummary', plate);
        newSchemaIdBySampleId[sample.id] = sample.storableSchema.id;
      }
    }

    // Memoizing the mapping is essential but tricky. Given the simplicity of the object,
    // and the fact that the inputs are ordered, a string is totally sufficient
    if (!isEqual(newSchemaIdBySampleId, schemaIdBySampleId)) {
      setSchemaIdsBySampleId(newSchemaIdBySampleId);
    }
  }

  return {status: queryResult.type, schemaIdBySampleId};
}
